/**
 * Shell voice session: push-to-talk → WAV STT → agent conversation turn.
 * Opens a background agent chat (default agent); closing the panel stops any
 * recording or pending reply, the session itself is reused on the next open.
 */

import { computed, ref, shallowRef } from 'vue';
import { useNxSocket } from '@blpde/connect';
import type { AgentRecordContext } from '@blpde/connect';
import {
  createConversation,
  listAgents,
  pickDefaultAgent,
  restChat,
  transcribeAudio,
  type ShellAgent,
  type ShellConversation,
  type ShellRecordContext,
} from '@/assistant/agentApi';
import { WavRecorder } from './recorder';

export type VoicePhase =
  | 'idle'
  | 'booting'
  | 'ready'
  | 'recording'
  | 'transcribing'
  | 'thinking'
  | 'error';

export interface VoiceTurn {
  id: string;
  role: 'user' | 'agent';
  content: string;
  pending?: boolean;
  error?: string;
}

const open = ref(false);
const phase = ref<VoicePhase>('idle');
const error = ref<string | null>(null);
const agent = shallowRef<ShellAgent | null>(null);
const conversation = shallowRef<ShellConversation | null>(null);
const context = shallowRef<AgentRecordContext | null>(null);
const turns = ref<VoiceTurn[]>([]);
const partialTranscript = ref('');

let recorder: WavRecorder | null = null;
let inFlightRequestId: string | null = null;
/** Bumped on every close, so a transcription started before it is dropped. */
let closeEpoch = 0;
let channelWired = false;
let frameHandler: ((frame: { type: string; requestId?: string; text?: string; error?: string }) => void) | null =
  null;

function toApiContext(ctx: AgentRecordContext | null | undefined): ShellRecordContext | null {
  if (!ctx?.moduleKey?.trim() || !ctx.resourceType?.trim() || !ctx.resourceId?.trim()) {
    return null;
  }
  return {
    module_key: ctx.moduleKey.trim(),
    resource_type: ctx.resourceType.trim(),
    resource_id: ctx.resourceId.trim(),
    ...(ctx.label?.trim() ? { label: ctx.label.trim() } : {}),
  };
}

function newId(): string {
  return `v-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function useShellVoice() {
  const socket = useNxSocket();

  if (socket && !channelWired) {
    channelWired = true;
    socket.channel('agent').on('data', (raw: unknown) => {
      frameHandler?.(raw as { type: string; requestId?: string; text?: string; error?: string });
    });
  }

  frameHandler = (frame) => {
    if (!inFlightRequestId || frame.requestId !== inFlightRequestId) return;
    switch (frame.type) {
      case 'text_delta': {
        const last = turns.value.at(-1);
        if (last?.role === 'agent' && last.pending) {
          last.content += frame.text ?? '';
          turns.value = [...turns.value];
        }
        break;
      }
      case 'agent_done': {
        const last = turns.value.at(-1);
        if (last?.role === 'agent') {
          last.pending = false;
          turns.value = [...turns.value];
        }
        phase.value = 'ready';
        inFlightRequestId = null;
        break;
      }
      case 'agent_error': {
        const last = turns.value.at(-1);
        if (last?.role === 'agent') {
          last.pending = false;
          last.error = frame.error || 'error';
          turns.value = [...turns.value];
        }
        phase.value = 'error';
        error.value = frame.error || 'error';
        inFlightRequestId = null;
        break;
      }
      default:
        break;
    }
  };

  const contextLine = computed(() => {
    const c = context.value;
    if (!c) return '';
    if (c.label?.trim()) return c.label.trim();
    return `${c.moduleKey}/${c.resourceType}#${c.resourceId}`;
  });
  const isRecording = computed(() => phase.value === 'recording');
  const isBusy = computed(
    () =>
      phase.value === 'booting' ||
      phase.value === 'transcribing' ||
      phase.value === 'thinking',
  );

  async function ensureSession(recordCtx?: AgentRecordContext | null) {
    if (agent.value && conversation.value) return;
    phase.value = 'booting';
    error.value = null;
    context.value = recordCtx?.moduleKey ? { ...recordCtx } : context.value;

    const agents = await listAgents();
    const picked = pickDefaultAgent(agents);
    if (!picked) {
      phase.value = 'error';
      error.value = 'no-agent';
      throw new Error('no-agent');
    }
    agent.value = picked;

    const apiCtx = toApiContext(context.value);
    const title =
      apiCtx?.label ||
      (apiCtx ? `Voice · ${apiCtx.resource_type}` : 'Sprachsteuerung');
    const conv = await createConversation(picked.id, {
      title,
      context: apiCtx,
    });
    conversation.value = conv;
    phase.value = 'ready';
  }

  async function openVoice(recordCtx?: AgentRecordContext | null) {
    open.value = true;
    error.value = null;
    partialTranscript.value = '';

    // Reuse the existing session (closing keeps it).
    if (agent.value && conversation.value) {
      phase.value = 'ready';
      if (recordCtx?.moduleKey) context.value = { ...recordCtx };
      return;
    }

    turns.value = [];
    try {
      await ensureSession(recordCtx ?? null);
    } catch (e: unknown) {
      phase.value = 'error';
      error.value = e instanceof Error ? e.message : 'error';
    }
  }

  async function startRecording() {
    if (isBusy.value || phase.value === 'recording') return;
    error.value = null;
    partialTranscript.value = '';
    try {
      if (!agent.value || !conversation.value) {
        await ensureSession(context.value);
      }
      recorder = new WavRecorder();
      await recorder.start();
      phase.value = 'recording';
    } catch (e: unknown) {
      phase.value = 'error';
      const msg = e instanceof Error ? e.message : 'error';
      error.value =
        msg === 'unsupported' || msg.includes('NotAllowed') || msg.includes('Permission')
          ? 'mic-denied'
          : msg;
      recorder = null;
    }
  }

  async function stopRecordingAndSend() {
    if (!recorder || phase.value !== 'recording') return;
    const epoch = closeEpoch;
    phase.value = 'transcribing';
    let wav: Blob;
    try {
      wav = await recorder.stop();
    } catch (e: unknown) {
      phase.value = 'error';
      error.value = e instanceof Error ? e.message : 'error';
      recorder = null;
      return;
    }
    recorder = null;
    if (epoch !== closeEpoch) return;

    if (wav.size < 1000) {
      phase.value = 'ready';
      error.value = 'too-short';
      return;
    }

    try {
      // WAV only — never FLAC (upstream 400).
      const result = await transcribeAudio(wav, 'speech.wav', { language: 'de' });
      if (epoch !== closeEpoch) return;
      const text = result.text.trim();
      partialTranscript.value = text;
      if (!text) {
        phase.value = 'ready';
        error.value = 'empty-transcript';
        return;
      }
      await sendTextToAgent(text);
    } catch (e: unknown) {
      phase.value = 'error';
      error.value = e instanceof Error ? e.message : 'error';
    }
  }

  async function cancelRecording() {
    if (recorder) {
      await recorder.cancel();
      recorder = null;
    }
    if (phase.value === 'recording') phase.value = 'ready';
  }

  async function sendTextToAgent(text: string) {
    const conv = conversation.value;
    const a = agent.value;
    if (!conv || !a) return;

    turns.value = [
      ...turns.value,
      { id: newId(), role: 'user', content: text },
      { id: newId(), role: 'agent', content: '', pending: true },
    ];
    phase.value = 'thinking';
    error.value = null;

    const requestId = `voice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    inFlightRequestId = requestId;

    if (socket) {
      try {
        const uiContext: Record<string, unknown> = {
          source: 'connect.shell.voice',
          ts: Date.now(),
        };
        if (context.value) {
          uiContext.record = {
            module_key: context.value.moduleKey,
            resource_type: context.value.resourceType,
            resource_id: context.value.resourceId,
            ...(context.value.label ? { label: context.value.label } : {}),
          };
        }
        socket.channel('agent').send({
          type: 'chat',
          requestId,
          conversationId: conv.id,
          agentId: a.id,
          message: text,
          uiContext,
        });
        return;
      } catch {
        /* REST fallback */
      }
    }

    try {
      const res = await restChat(a.id, conv.id, text);
      const last = turns.value.at(-1);
      if (last?.role === 'agent') {
        last.content = res.content;
        last.pending = false;
        turns.value = [...turns.value];
      }
      phase.value = 'ready';
    } catch (e: unknown) {
      const last = turns.value.at(-1);
      if (last?.role === 'agent') {
        last.pending = false;
        last.error = e instanceof Error ? e.message : 'error';
        turns.value = [...turns.value];
      }
      phase.value = 'error';
      error.value = e instanceof Error ? e.message : 'error';
    } finally {
      inFlightRequestId = null;
    }
  }

  function closeVoice() {
    closeEpoch += 1;
    void cancelRecording();
    // A reply still streaming has no panel to land in: tell the agent to stop.
    if (socket && inFlightRequestId) {
      try {
        socket.channel('agent').send({ type: 'cancel', requestId: inFlightRequestId });
      } catch {
        /* the stream ends on its own */
      }
      const last = turns.value.at(-1);
      if (last?.role === 'agent' && last.pending) {
        last.pending = false;
        turns.value = [...turns.value];
      }
    }
    open.value = false;
    phase.value = 'idle';
    inFlightRequestId = null;
  }

  return {
    open,
    phase,
    error,
    agent,
    conversation,
    context,
    contextLine,
    turns,
    partialTranscript,
    isRecording,
    isBusy,
    openVoice,
    closeVoice,
    startRecording,
    stopRecordingAndSend,
    cancelRecording,
  };
}
